import { computed, ComputedRef } from 'vue'

import { LineupConfig, LineupModel } from '@collector/desktop-feature-lineups'
import { Event, Participant, SubParticipant } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'

import { useEvent, useSeason, useSport } from '../../../composables'
import { injectLineupConfig } from '../../provide-inject'
import { useProbableLineupDeferredClear } from '../useProbableLineupDeferredClear'
import {
  createDefaultConfig,
  createDefaultLineupModel,
  hasPositionChangedDefault,
  toSortedLineupModelDefault,
} from './utils'

export type UpdateLineupsFn = (
  eventId: Event['id'],
  participantId: Participant['id'],
  lineup: LineupModel<PropertyKey>,
) => Promise<void>

type UseLineupConfig = {
  config: ComputedRef<LineupConfig>
  updateLineups: UpdateLineupsFn
  createLineupModel: (players: SubParticipant[]) => LineupModel<PropertyKey>
  toSortedLineupModel: (
    lineup: LineupModel<PropertyKey>,
  ) => LineupModel<PropertyKey>
  hasPositionChanged: (
    key: PropertyKey,
    lineup: LineupModel,
    initialLineup: LineupModel,
  ) => boolean
  sortSquad?: (a: SubParticipant, b: SubParticipant) => number
  transformSubParticipants?: (
    eventId: Event['id'],
    players: SubParticipant[],
  ) => Promise<SubParticipant[]>
}

export function useLineupConfig(): UseLineupConfig {
  const { sport } = useSport()
  const { isPlayerSearchAllowed } = useSeason()
  const { event } = useEvent()

  const {
    config,
    createLineupModel,
    toSortedLineupModel,
    updateLineups,
    hasPositionChanged,
    sortSquad,
    transformSubParticipants,
  } = injectLineupConfig()

  const { runDeferredClearLineupAfterSave } = useProbableLineupDeferredClear()

  const lConfig = config ?? createDefaultConfig()

  const updateLineupsWithDeferredClear: UpdateLineupsFn = async (
    eventId,
    participantId,
    lineup,
  ) => {
    await updateLineups(eventId, participantId, lineup)
    await runDeferredClearLineupAfterSave(sportsApiClient, participantId)
  }

  return {
    config: computed(() => {
      return {
        ...lConfig,
        addPlayer: {
          isSearchAllowed: isPlayerSearchAllowed(),
          sportId: sport.value.id,
          gender: event.value.gender,
          ...lConfig.addPlayer,
        },
        display: { highlightMissingId: !isPlayerSearchAllowed() },
      }
    }),
    createLineupModel: createLineupModel ?? createDefaultLineupModel,
    toSortedLineupModel: toSortedLineupModel ?? toSortedLineupModelDefault,
    updateLineups: updateLineupsWithDeferredClear,
    hasPositionChanged: hasPositionChanged ?? hasPositionChangedDefault,
    sortSquad,
    transformSubParticipants,
  }
}
